import { KONE_ROOT_VARS } from './koneColors';

/**
 * Styles for the Compliance Management plugin's CompliancePage.
 *
 * Ported from dummy_webpage/current/view3.css, EXCLUDING the app-shell/
 * sidebar/page-header chrome sections — real Backstage already renders that
 * chrome around every page via <Page>/<Header>/<Content>, so
 * duplicating it here would fight the real theme instead of complementing
 * it (same reasoning as business_applications' details/styles.ts).
 *
 * Also includes the small set of shared chrome classes (.card,
 * .detail-container, .dashboard-grid, .dashboard-card, .detail-view-*,
 * .back-button, .form-control, .loading-text) that view3.css assumed it
 * could inherit from view2.css via <link> — since this plugin has no
 * dependency on @internal/backstage-plugin-business-applications, those class
 * definitions are copied here too (verbatim values) rather than imported
 * cross-plugin. If they drift from the business_applications copy over
 * time, that is an acceptable tradeoff for keeping the two plugins
 * independent, per product decision.
 */
export const COMPLIANCE_PAGE_STYLES = `
:root {
${KONE_ROOT_VARS}
}

/* ---------- Shared chrome (subset needed by this page; see docstring) ---------- */

.detail-container {
  display: grid;
  gap: 16px;
}

.loading-text {
  font-size: 14px;
  color: var(--kone-text-muted);
}

.card,
.primary_card {
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(20, 80, 245, 0.04);
}

.primary_card {
  background: linear-gradient(135deg, var(--kone-sand) 0%, var(--kone-surface) 100%);
}

.card h4,
.primary_card h4 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--kone-text);
}

.card p,
.primary_card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--kone-text-muted);
}

.evaluation-heading {
  margin-top: 20px;
}

.modules-card h4 {
  margin-bottom: 16px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 720px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

.dashboard-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 96px;
  padding: 18px 16px;
  border: none;
  border-radius: 12px;
  background: var(--kone-blue);
  color: var(--kone-white);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  box-shadow: 0 2px 6px rgba(20, 80, 245, 0.18);
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(20, 80, 245, 0.28);
  filter: brightness(1.06);
}

.dashboard-card:active {
  transform: translateY(0);
  filter: brightness(0.97);
}

.dashboard-card:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.4);
  outline-offset: 3px;
}

/* Same specificity-override reasoning as business_applications' details/styles.ts:
   .card p already sets color: var(--kone-text-muted), and .dashboard-grid
   sits inside a div carrying class "card" (see CompliancePage.tsx's card
   modules-card wrapper div), so that descendant selector would otherwise
   win over the plain .dashboard-card { color: white } rule and make the
   label dark text on a blue background. */
.dashboard-grid .dashboard-card-icon,
.dashboard-grid .dashboard-card-label {
  color: var(--kone-white);
}

.dashboard-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.dashboard-card-label {
  line-height: 1.3;
}

.detail-view-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 16px;
}

.detail-view-card {
  /* .card already supplies background/border/padding/radius/shadow */
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--kone-blue);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
}

.back-button:hover {
  text-decoration: underline;
}

.detail-view-title {
  margin: 0;
}

.form-control {
  width: 100%;
  max-width: 320px;
  padding: 8px 10px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
}

.action-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  background-color: var(--kone-blue);
  color: var(--kone-white) !important;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font: inherit;
  font-weight: 600;
}

.action-button:hover {
  background-color: var(--kone-blue);
  opacity: 0.92;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.secondary-button {
  background-color: var(--kone-surface-alt);
  color: var(--kone-text) !important;
  border: 1px solid var(--kone-border);
}

.secondary-button:hover {
  background-color: var(--kone-border);
  opacity: 1;
}

.danger-button {
  background-color: #b42318;
  color: var(--kone-white) !important;
}

.danger-button:hover {
  background-color: #912018;
  opacity: 1;
}

.inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
}

/* ---------- Compliance-specific styles (from dummy_webpage's view3.css) ---------- */

.compliance-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 900px) {
  .compliance-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.compliance-stat-card {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
  font: inherit;
}

.compliance-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(20, 80, 245, 0.12);
}

.compliance-stat-icon {
  font-size: 20px;
}

.compliance-stat-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--kone-text);
  line-height: 1;
}

.compliance-stat-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--kone-text-muted);
  text-transform: uppercase;
}

.compliance-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-end;
  margin-top: 4px;
}

.compliance-filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.compliance-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--kone-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.01em;
}

.compliance-filter-options {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.compliance-filter-btn {
  border: 1px solid var(--kone-border);
  background: var(--kone-surface);
  color: var(--kone-text);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  font: inherit;
}

.compliance-filter-btn:hover {
  border-color: var(--kone-blue);
}

.compliance-filter-btn.is-selected {
  background: var(--kone-blue);
  border-color: var(--kone-blue);
  color: var(--kone-white);
}

.compliance-filter-summary {
  margin-top: 4px;
  font-size: 13px;
  color: var(--kone-text-muted);
}

.compliance-filter-summary strong {
  color: var(--kone-text);
}

.compliance-search-row {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.compliance-search-input {
  flex: 1 1 260px;
  max-width: 420px;
}

.compliance-result-count {
  font-size: 13px;
  color: var(--kone-text-muted);
}

.compliance-list {
  margin-top: 16px;
  display: grid;
  gap: 10px;
}

.compliance-item {
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
  overflow: hidden;
}

.compliance-item-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  background: transparent;
  padding: 12px 16px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: var(--kone-text);
}

.compliance-item-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.compliance-item-name {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compliance-item-chevron {
  flex: 0 0 auto;
  color: var(--kone-text-muted);
  transition: transform 0.2s ease;
}

.compliance-item.is-expanded .compliance-item-chevron {
  transform: rotate(180deg);
}

.compliance-item-badge {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.compliance-item-badge--required {
  background: rgba(255, 160, 35, 0.16);
  color: #8A5A00;
}

.compliance-item-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.compliance-item.is-expanded .compliance-item-body {
  grid-template-rows: 1fr;
}

.compliance-item-body-inner {
  overflow: hidden;
  min-height: 0;
  padding: 0 16px;
}

.compliance-item.is-expanded .compliance-item-body-inner {
  padding: 0 16px 16px;
}

.compliance-item-desc {
  font-size: 13px;
  line-height: 1.6;
  color: var(--kone-text-muted);
  white-space: pre-line;
  margin: 0 0 10px;
}

.compliance-item-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.compliance-item-links a {
  font-size: 12px;
  color: var(--kone-blue);
}

.compliance-item-links a:hover {
  text-decoration: underline;
}

.compliance-item-standards {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.compliance-standard-chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--kone-light-blue);
  color: var(--kone-blue);
}

.compliance-empty-state {
  padding: 24px;
  text-align: center;
  color: var(--kone-text-muted);
  font-size: 14px;
  font-style: italic;
}

.compliance-usecase {
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
}

.compliance-usecase-body {
  padding: 12px 16px;
  display: grid;
  gap: 10px;
}

.compliance-threat-group {
  border: 1px solid var(--kone-border);
  border-radius: 10px;
  background: var(--kone-surface);
  overflow: hidden;
}

.compliance-threat-group .compliance-item-toggle {
  padding: 10px 14px;
}

.compliance-threat-group .compliance-item-body-inner {
  padding: 0 14px;
}

.compliance-threat-group.is-expanded .compliance-item-body-inner {
  padding: 0 14px 14px;
}

.compliance-threat-card {
  border: 1px solid var(--kone-border);
  border-radius: 10px;
  padding: 12px 14px;
  background: var(--kone-surface);
}

.compliance-threat-name {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--kone-text);
}

.compliance-threat-desc {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--kone-text-muted);
  white-space: pre-line;
}

.compliance-threat-countermeasures {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 4px;
}

.compliance-threat-countermeasures li {
  font-size: 12px;
  color: var(--kone-text-muted);
}

.compliance-mitigation-pct {
  font-weight: 600;
  color: var(--kone-blue);
}

/* ---------- "Add Requirement / Control" — the editable library-entry list ---------- */

.compliance-library-entry {
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  background: var(--kone-light-blue);
  padding: 12px 16px;
}

.compliance-add-form {
  margin-top: 16px;
  padding: 16px;
  border: 1px dashed var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
  display: grid;
  gap: 12px;
}

.compliance-add-form-field {
  display: grid;
  gap: 6px;
}

.compliance-add-form-field label {
  font-size: 13px;
  font-weight: 600;
  color: var(--kone-text);
}

.compliance-add-form textarea.form-control {
  max-width: none;
  min-height: 80px;
  resize: vertical;
  font: inherit;
}

.compliance-requirement-form .form-control {
  max-width: none;
}

.compliance-requirement-form textarea.form-control {
  min-height: 200px;
}

.compliance-assignment-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.compliance-assignment-list {
  display: grid;
  gap: 8px;
  max-height: 320px;
  overflow: auto;
  padding: 8px 0;
}

.compliance-assignment-list label {
  display: block;
  font-size: 13px;
  color: var(--kone-text);
}

.dashboard-card small {
  color: var(--kone-white);
  font-size: 11px;
  font-weight: 400;
}

.compliance-requirement-desc {
  white-space: pre-line;
}

.compliance-library-entry strong {
  color: var(--kone-text);
}

/* Modal Styles for Link Picker */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  z-index: 0;
}

.modal-content {
  position: relative;
  z-index: 1;
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--kone-border);
}

.modal-header h3 {
  margin: 0;
  color: var(--kone-text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--kone-text-muted);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--kone-border);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
}

.link-target-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.link-target-filters .form-control {
  max-width: none;
}

.link-target-search-input {
  width: 410px;
  max-width: 100%;
}

.link-target-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  margin-top: 8px;
}

.link-target-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--kone-border);
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    transform 0.1s;
}

.link-target-option:hover {
  background: var(--kone-surface-hover);
}

.link-target-option:active {
  transform: scale(0.995);
}

.link-target-option:focus-visible {
  outline: 2px solid var(--kone-primary);
  outline-offset: -2px;
}

.link-target-option.selected {
  padding-left: 12px;
  border-left: 4px solid var(--kone-blue);
  background: var(--kone-blue);
  color: var(--kone-white);
}

.link-target-option.selected .target-standard {
  color: rgba(255, 255, 255, 0.85);
}

.link-target-selected-label {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  color: var(--kone-white);
  font-size: 12px;
  font-weight: 600;
}

.link-target-option:last-child {
  border-bottom: none;
}

.target-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.target-standard {
  font-size: 12px;
  color: var(--kone-text-muted);
}

.content-selection {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--kone-border);
}

.content-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.radio-option:hover {
  border-color: var(--kone-primary);
}

.radio-option input[type="radio"] {
  margin: 0;
  flex-shrink: 0;
}

.form-error {
  color: var(--kone-error);
  font-size: 14px;
  margin-right: auto;
}
`;
